import { FlexItem, Loader } from '@etsglobal/design-system';
import * as React from 'react';
import { Navigate, type RouteObject } from 'react-router';

import { PATHS } from '@/constants';
import { GoogleRecaptchaV3 } from '@/containers/GoogleRecaptchaV3';

import { AnonymousGuardProvider } from '@/contexts/anonymousGuard';
import { AuthenticatedGuardProvider } from '@/contexts/authenticatedGuard';
import { ScoringProvider } from '@/contexts/scoringContext';

const loadable = function <Props = any>(factory: Parameters<typeof React.lazy>[0]): React.FC<Props> {
  const Loadable: React.FC<Props> = (props: Props) => {
    const Component = React.lazy(factory);

    return (
      <React.Suspense
        fallback={
          <FlexItem flex="1" m="16" justifyContent="center">
            <Loader />
          </FlexItem>
        }
      >
        <Component {...props} />
      </React.Suspense>
    );
  };
  Loadable.displayName = 'Loadable';
  return Loadable;
};

const AppLayoutTemplateContainer = loadable(async () => await import('@/containers/AppLayoutTemplateContainer'));
const ScoringDashboardPageContainer = loadable(async () => await import('@/containers/ScoringDashboardPageContainer'));
const PrintingDashboardPageContainer = loadable(
  async () => await import('@/containers/PrintingDashboardPageContainer')
);
const LoginPageContainer = loadable(async () => await import('@/containers/LoginPageContainer'));
const NotFoundPageContainer = loadable(async () => await import('@/containers/NotFoundPageContainer'));
const ScoringTemplateContainer = loadable(async () => await import('@/containers/ScoringTemplateContainer'));
const QualityControlContainer = loadable(
  async () => await import('@/containers/ScoringTemplateContainer/QualityControlContainer')
);
const ScoreMatchingContainer = loadable(
  async () => await import('@/containers/ScoringTemplateContainer/ScoreMatchingContainer')
);
const CandidatesOptionsContainer = loadable(
  async () => await import('@/containers/ScoringTemplateContainer/CandidatesOptionsContainer')
);
const CandidatesScoreDisplayContainer = loadable(
  async () => await import('@/containers/ScoringTemplateContainer/CandidateScoreDisplayContainer')
);
const ScoringValidationContainer = loadable(
  async () => await import('@/containers/ScoringTemplateContainer/ScoringValidationContainer')
);
const ScoringEditContainer = loadable(async () => await import('@/containers/ScoringEditContainer'));
const IndividualScoreUpdateContainer = loadable(
  async () => await import('@/containers/IndividualScoreUpdateContainer')
);
const SessionDetailsPageContainer = loadable(async () => await import('@/containers/SessionDetailsPageContainer'));

export const routes: RouteObject[] = [
  {
    path: PATHS.ROOT,
    element: (
      <AuthenticatedGuardProvider>
        <AppLayoutTemplateContainer />
      </AuthenticatedGuardProvider>
    ),
    children: [
      {
        index: true,
        element: <Navigate to={PATHS.SCORING_DASHBOARD} replace={true} />,
      },
      { path: PATHS.PRINTING_DASHBOARD, element: <PrintingDashboardPageContainer /> },
      { path: PATHS.SCORING_DASHBOARD, element: <ScoringDashboardPageContainer /> },
      {
        path: `${PATHS.SCORING}:sessionId`,
        element: (
          <ScoringProvider>
            <ScoringTemplateContainer />
          </ScoringProvider>
        ),
        children: [
          {
            index: true,
            element: <QualityControlContainer />,
          },
          {
            path: PATHS.QUALITY_CONTROL,
            element: <QualityControlContainer />,
          },
          {
            path: PATHS.SCORE_MATCHING,
            element: <ScoreMatchingContainer />,
          },
          {
            path: PATHS.CANDIDATES_SCORE_DISPLAY,
            element: <CandidatesScoreDisplayContainer />,
          },
        ],
      },
      {
        path: `${PATHS.SCORING}:sessionId/${PATHS.SCORING_VALIDATION}`,
        element: <ScoringValidationContainer />,
      },
      {
        path: `${PATHS.SCORING}:sessionId/${PATHS.CANDIDATE_OPTIONS}`,
        element: <CandidatesOptionsContainer />,
      },
      { path: `${PATHS.SCORING}:sessionId/${PATHS.SCORE_EDIT}`, element: <ScoringEditContainer /> },
      { path: `${PATHS.SCORING}:sessionId/${PATHS.SCORE_UPDATE}:scoreId`, element: <IndividualScoreUpdateContainer /> },
      { path: `${PATHS.SESSION_DETAILS}/:sessionId`, element: <SessionDetailsPageContainer /> },
      { path: '*', element: <NotFoundPageContainer /> },
    ],
  },
  {
    path: PATHS.LOGIN,
    element: (
      <GoogleRecaptchaV3>
        <AnonymousGuardProvider>
          <LoginPageContainer />
        </AnonymousGuardProvider>
      </GoogleRecaptchaV3>
    ),
  },
];
